/**
 * The InputManager (ARCHITECTURE §8 "Input"): keyboard layouts (solo / p1 / p2), every connected gamepad
 * (`pad:<n>`, hot-plug), on-screen touch controls, per-device rebinding, menu navigation from all devices,
 * rebinding capture and haptics.
 *
 * Platform-neutral: every event source is injected (`InputHost`): a key-event target, a Gamepad-API-shaped source,
 * a touch-overlay factory, touch detection and a vibration pulse. The web wires the DOM (apps/client
 * `src/input/manager.ts` `createInputManager`); a native shell wires its touch view and gamepad module.
 *
 * Per tick the match loop calls `sample(slot)` for each slot and then `endTick()`. The first `sample` of a tick
 * polls the Gamepad API once; between ticks a display-frame poll keeps menus responsive and latches sub-tick pad
 * taps. `sample()` itself never allocates.
 */
import { BUTTON_NAMES, type ButtonName, type InputFrame } from '@hf/shared';
import {
  cloneBindings,
  defaultBindingsFor,
  KEYBOARD_P1,
  KEYBOARD_P2,
  KEYBOARD_SOLO,
  resolveSharedKeyboard,
  sanitizeBindings,
  sharedKeyboardPartner,
} from './bindings';
import { PadHub, type GamepadDevice, type GamepadSource } from './gamepad';
import {
  isTypingTarget,
  KeyboardDevice,
  KeyboardState,
  type InputEventTarget,
  type KeyEventLike,
} from './keyboard';
import { codeLabel } from './labels';
import { TouchDevice, TouchState } from './touch';
import {
  DEVICE_ID,
  padIndexOf,
  type AuxCommand,
  type Bindings,
  type CaptureOptions,
  type InputDevice,
  type InputManager,
  type MenuAction,
  type MenuSource,
  type TouchMode,
  type TouchStatus,
} from './types';

const MENU_KEYS: Record<string, MenuAction> = {
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
  Enter: 'confirm',
  NumpadEnter: 'confirm',
  Space: 'confirm',
  KeyJ: 'confirm',
  Escape: 'back',
  Backspace: 'back',
  KeyP: 'pause',
};
/** Menu directions auto-repeat on held keys (OS key repeat), like the pads' D-pad. */
const REPEATING_MENU: ReadonlySet<MenuAction> = new Set<MenuAction>(['up', 'down', 'left', 'right']);
/** Pad capture: Start cancels; holding B (the universal back) this long cancels, a tap binds B. */
const PAD_START = '9';
const PAD_B = '1';
export const CAPTURE_CANCEL_HOLD_MS = 600;
/** Keyboard keys of the aux commands (each ignored if the player bound it to a game button). */
const AUX_KEYS: Readonly<Record<string, AuxCommand>> = { KeyR: 'select', Tab: 'swap', KeyN: 'skip' };
/** Text fields, sliders, selects… keep their native keys (isTypingTarget already covers text + contenteditable). */
const isFormControl = (target: unknown): boolean => {
  if (isTypingTarget(target)) return true;
  const tag = (target as { tagName?: unknown } | null)?.tagName;
  return typeof tag === 'string' && ['INPUT', 'SELECT', 'TEXTAREA'].includes(tag.toUpperCase());
};
const KEYBOARD_SOURCE: MenuSource = { deviceId: DEVICE_ID.KB_SOLO, kind: 'keyboard' };
const TOUCH_SOURCE: MenuSource = { deviceId: DEVICE_ID.TOUCH, kind: 'touch' };

/** An on-screen touch pad (web: a DOM overlay; native: a touch view). The manager only shows / hides it. */
export interface TouchOverlayLike {
  setVisible(visible: boolean): void;
  readonly isVisible: boolean;
  dispose(): void;
}

/** The platform's input sources. Everything is optional: a missing source is simply absent (tests, tools). */
export interface InputHost {
  /**
   * Key events (`keydown` / `keyup` / `blur`, KeyboardEvent-shaped: `code`, `repeat`, `target`…) and the first-touch
   * probe (`pointerdown` with `pointerType`). Null = no keyboard.
   */
  target?: InputEventTarget | null;
  /** Gamepad-API-shaped pad source (null = no pads). */
  gamepads?: GamepadSource | null;
  /** Clock for menu auto-repeat (default performance.now). */
  now?: () => number;
  /** Display-frame scheduler for the menu/hot-plug poll (default requestAnimationFrame when it exists). */
  requestFrame?: ((cb: () => void) => number) | null;
  cancelFrame?: (id: number) => void;
  /** Build the on-screen touch pad, fed through `state` (null = none). `onPause` = its pause button. */
  createTouchOverlay?: ((state: TouchState, opts: { onPause(): void }) => TouchOverlayLike) | null;
  /** Touch-screen detection for touch mode 'auto' (default: none). */
  touchCapable?: () => boolean;
  /** A short vibration on a touch-button press (default: none). */
  vibrate?: ((ms: number) => void) | null;
}

export const createInputManagerWith = (opts: InputHost = {}): InputManager => {
  const target = opts.target ?? null;
  const now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  const requestFrame =
    opts.requestFrame === undefined
      ? typeof requestAnimationFrame === 'function'
        ? (cb: () => void) => requestAnimationFrame(cb)
        : null
      : opts.requestFrame;
  const cancelFrame =
    opts.cancelFrame ??
    ((id: number) => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(id) : undefined));

  // ── Devices ───────────────────────────────────────────────────────────────────────────────────────
  const keys = new KeyboardState(target);
  const keyboards = [
    new KeyboardDevice(DEVICE_ID.KB_SOLO, 'Keyboard', keys, KEYBOARD_SOLO),
    new KeyboardDevice(DEVICE_ID.KB_P1, 'Keyboard (left)', keys, KEYBOARD_P1),
    new KeyboardDevice(DEVICE_ID.KB_P2, 'Keyboard (right)', keys, KEYBOARD_P2),
  ];
  const touchState = new TouchState();
  const touch = new TouchDevice(touchState);
  const deviceHandlers = new Set<(d: readonly InputDevice[]) => void>();
  const menuHandlers = new Set<(a: MenuAction, s: MenuSource) => void>();
  const auxHandlers = new Set<(c: AuxCommand, s: MenuSource) => void>();
  const touchHandlers = new Set<(t: TouchStatus) => void>();
  let lastActive: string | null = null;
  let capture: { deviceId: string; resolve: (code: string | null) => void } | null = null;
  /** Pad capture: B went down at this time; released before CAPTURE_CANCEL_HOLD_MS = bind B, held = cancel. */
  let pendingB: { padIndex: number; at: number } | null = null;
  /** A pad whose press just resolved a capture: its menu edges of that same poll are swallowed. */
  let swallowPadMenu: string | null = null;
  const pollPads = (): void => {
    swallowPadMenu = null;
    pads.poll(now());
  };

  const emitMenu = (a: MenuAction, s: MenuSource): void => {
    if (capture) return;
    for (const h of [...menuHandlers]) h(a, s);
  };
  const emitAux = (c: AuxCommand, s: MenuSource): void => {
    if (capture) return;
    for (const h of [...auxHandlers]) h(c, s);
  };

  const pads = new PadHub(opts.gamepads ?? null, {
    devicesChanged: () => {
      rebuildList();
      // A pulled pad must not leave stale capture pending.
      if (
        capture &&
        padIndexOf(capture.deviceId) >= 0 &&
        !pads.device(padIndexOf(capture.deviceId))?.connected
      )
        finishCapture(null);
    },
    menu: (a, pad) => {
      if (pad.id !== swallowPadMenu) emitMenu(a, { deviceId: pad.id, kind: 'gamepad' });
    },
    select: (pad) => emitAux('select', { deviceId: pad.id, kind: 'gamepad' }),
    rightStickClick: (pad) => emitAux('swap', { deviceId: pad.id, kind: 'gamepad' }),
    rawPress: (pad, code) => {
      lastActive = pad.id;
      if (!capture || capture.deviceId !== pad.id) return;
      if (code === PAD_B) {
        pendingB ??= { padIndex: pad.index, at: now() };
        return;
      }
      if (pendingB) return;
      finishCapture(code === PAD_START ? null : code);
      // The press that ended the capture must not also act on the menu (Start = pause) in the same poll.
      swallowPadMenu = pad.id;
    },
  });

  let list: InputDevice[] = [];
  const rebuildList = (): void => {
    list = [...keyboards, ...pads.connected, ...(touch.connected ? [touch] : [])];
    for (const h of [...deviceHandlers]) h(list);
  };

  // ── Touch overlay ─────────────────────────────────────────────────────────────────────────────────
  let touchMode: TouchMode = 'auto';
  let touchSuspended = false;
  let touchSeen = false;
  const touchCapable = opts.touchCapable ?? (() => false);
  const createOverlay = opts.createTouchOverlay ?? null;
  let overlay: TouchOverlayLike | null = null;
  const ensureOverlay = (): TouchOverlayLike | null => {
    if (!overlay && createOverlay) {
      overlay = createOverlay(touchState, {
        onPause: () => {
          lastActive = DEVICE_ID.TOUCH;
          emitMenu('pause', TOUCH_SOURCE);
        },
      });
    }
    return overlay;
  };
  touchState.onPress = () => {
    lastActive = DEVICE_ID.TOUCH;
    opts.vibrate?.(8);
  };
  const touchAvailable = (): boolean =>
    touchMode === 'on' || (touchMode === 'auto' && (touchSeen || touchCapable()));
  const updateTouch = (): void => {
    const available = touchAvailable();
    if (available !== touch.connected) {
      touch.connected = available;
      rebuildList();
    }
    const assignedTouch = slotIds[0].includes(DEVICE_ID.TOUCH) || slotIds[1].includes(DEVICE_ID.TOUCH);
    const show = available && assignedTouch && !touchSuspended;
    if (show) ensureOverlay()?.setVisible(true);
    else overlay?.setVisible(false);
    emitTouch();
  };
  /** Last touch status announced to `onTouchChanged` handlers (mode | available | visible). */
  let touchSig = '';
  const touchSignature = (): string => `${touchMode}|${touch.connected}|${overlay?.isVisible ?? false}`;
  const emitTouch = (): void => {
    const sig = touchSignature();
    if (sig === touchSig) return;
    touchSig = sig;
    for (const h of [...touchHandlers]) h(manager.touch);
  };
  const onFirstTouch = (e: { pointerType?: string }): void => {
    if (e.pointerType !== 'touch' || touchSeen) return;
    touchSeen = true;
    updateTouch();
  };
  target?.addEventListener('pointerdown', onFirstTouch, true);

  // ── Keyboard menu / aux / activity ────────────────────────────────────────────────────────────────
  /** Key events consumed by a rebinding capture (the capture listener runs first, in the capture phase). */
  const captured = new WeakSet<object>();
  keys.onPress = (code, e) => {
    lastActive = keyboards.find((k) => isAssigned(k.id) && k.uses(code))?.id ?? DEVICE_ID.KB_SOLO;
    if (!capture) return;
    const keyboardCapture = padIndexOf(capture.deviceId) < 0 && capture.deviceId !== DEVICE_ID.TOUCH;
    // Escape cancels any capture (a pad capture too); other keys only resolve a keyboard capture.
    if (code !== 'Escape' && !keyboardCapture) return;
    if (e) {
      captured.add(e);
      e.preventDefault?.();
    }
    finishCapture(code === 'Escape' ? null : code);
  };
  /** A game key of a keyboard assigned to a match slot. */
  const isGameKey = (code: string): boolean => keyboards.some((k) => isAssigned(k.id) && k.uses(code));
  const onKeyDown = (e: KeyEventLike): void => {
    if (capture || captured.has(e) || isTypingTarget(e.target)) return;
    const action = MENU_KEYS[e.code];
    const gameKey = action !== undefined || AUX_KEYS[e.code] !== undefined ? isGameKey(e.code) : false;
    // Held arrows step through lists and sliders (OS key repeat), but never while they also move a player.
    if (e.repeat && !(action && REPEATING_MENU.has(action) && !gameKey)) return;
    // P / Backspace bound as game buttons play, they do not pause (Escape always can: it is never bindable).
    if (action && !((action === 'pause' || action === 'back') && gameKey && e.code !== 'Escape'))
      emitMenu(action, KEYBOARD_SOURCE);
    const aux = AUX_KEYS[e.code];
    if (aux && auxHandlers.size > 0 && !gameKey) {
      // Tab would move DOM focus in the middle of a match.
      if (e.code === 'Tab') e.preventDefault?.();
      emitAux(aux, KEYBOARD_SOURCE);
    }
  };
  target?.addEventListener('keydown', onKeyDown);
  /**
   * While a slot uses a keyboard layout (a match is on), its game keys must not trigger browser defaults: Firefox
   * Quick Find on '/' (P2's Special fallback) steals focus mid-match, Space/arrows scroll, F-keys or Quote after
   * a rebind… Modifier chords (browser shortcuts) and form controls keep their defaults. Repeats are included.
   */
  const onGameKey = (e: KeyEventLike): void => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isFormControl(e.target)) return;
    if (slotIds[0].length === 0 && slotIds[1].length === 0) return;
    if (keyboards.some((k) => isAssigned(k.id) && k.uses(e.code))) e.preventDefault?.();
  };
  target?.addEventListener('keydown', onGameKey, true);

  // ── Slots ─────────────────────────────────────────────────────────────────────────────────────────
  const slotIds: [string[], string[]] = [[], []];
  /** Resolved device objects per slot (rebuilt on assign / hot-plug; sample() only reads it). */
  const slotDevices: [InputDevice[], InputDevice[]] = [[], []];
  const isAssigned = (id: string): boolean => slotIds[0].includes(id) || slotIds[1].includes(id);
  const resolveSlots = (): void => {
    for (const s of [0, 1] as const) {
      const out: InputDevice[] = [];
      for (const id of slotIds[s]) {
        const pi = padIndexOf(id);
        if (pi >= 0) {
          const pad = pads.device(pi);
          if (pad && !out.includes(pad)) out.push(pad);
        } else if (id === DEVICE_ID.ANY_PAD) {
          const other = slotIds[s === 0 ? 1 : 0];
          for (const pad of pads.connected) if (!other.includes(pad.id) && !out.includes(pad)) out.push(pad);
        } else if (id === DEVICE_ID.TOUCH) out.push(touch);
        else {
          const kb = keyboards.find((k) => k.id === id);
          if (kb) out.push(kb);
        }
      }
      slotDevices[s] = out;
    }
  };
  deviceHandlers.add(resolveSlots);

  // ── Polling ───────────────────────────────────────────────────────────────────────────────────────
  let polledThisTick = false;
  let frameId: number | null = null;
  const framePoll = (): void => {
    frameId = null;
    pollPads();
    checkPendingB();
    if (requestFrame) frameId = requestFrame(framePoll);
  };
  /** Pad capture with B down: a tap binds B, a hold cancels (checked every display frame + by a timer). */
  const checkPendingB = (): void => {
    const p = pendingB;
    if (!p) return;
    if (!capture) {
      pendingB = null;
      return;
    }
    const held = ((pads.device(p.padIndex)?.raw.buttons ?? 0) & (1 << Number(PAD_B))) !== 0;
    if (!held) finishCapture(PAD_B);
    else if (now() - p.at >= CAPTURE_CANCEL_HOLD_MS) finishCapture(null);
  };
  if (requestFrame) frameId = requestFrame(framePoll);

  // ── Rebinding ─────────────────────────────────────────────────────────────────────────────────────
  const overrides = new Map<string, Bindings>();
  /**
   * The shared keyboard's halves (kb:p1 / kb:p2) play at once in local Versus: a key drives only one of them. Their
   * EFFECTIVE bindings resolve conflicts in favour of the half rebound / reset last (at boot: the half with saved
   * overrides, else P1); `overrides` keeps what was configured.
   */
  const effectiveKb = new Map<string, Bindings>();
  let kbWinner: 'kb:p1' | 'kb:p2' = DEVICE_ID.KB_P1;
  let kbTouched = false;
  const applySharedKeyboard = (): void => {
    const r = resolveSharedKeyboard(
      overrides.get(DEVICE_ID.KB_P1) ?? defaultBindingsFor(DEVICE_ID.KB_P1),
      overrides.get(DEVICE_ID.KB_P2) ?? defaultBindingsFor(DEVICE_ID.KB_P2),
      kbWinner,
    );
    effectiveKb.set(DEVICE_ID.KB_P1, r.p1);
    effectiveKb.set(DEVICE_ID.KB_P2, r.p2);
    for (const kb of keyboards) {
      if (kb.id === DEVICE_ID.KB_P1) kb.bindings = r.p1;
      else if (kb.id === DEVICE_ID.KB_P2) kb.bindings = r.p2;
    }
  };
  const putBindings = (deviceId: string, bindings: Bindings | null): void => {
    if (bindings) overrides.set(deviceId, cloneBindings(bindings));
    else overrides.delete(deviceId);
    const pi = padIndexOf(deviceId);
    if (pi >= 0) pads.setBindings(pi, bindings);
    else if (sharedKeyboardPartner(deviceId) === null) {
      const kb = keyboards.find((k) => k.id === deviceId);
      if (kb) kb.bindings = bindings ?? defaultBindingsFor(deviceId);
    }
  };
  const touchSharedKeyboard = (deviceId: string): void => {
    if (deviceId !== DEVICE_ID.KB_P1 && deviceId !== DEVICE_ID.KB_P2) return;
    kbWinner = deviceId;
    kbTouched = true;
    applySharedKeyboard();
  };
  const finishCapture = (code: string | null): void => {
    const c = capture;
    capture = null;
    pendingB = null;
    c?.resolve(code);
  };

  const manager: InputManager = {
    devices: () => list,
    onDevicesChanged(handler) {
      deviceHandlers.add(handler);
      return () => void deviceHandlers.delete(handler);
    },
    assign(slot, deviceIds) {
      slotIds[slot] = [...new Set(deviceIds)];
      resolveSlots();
      updateTouch();
    },
    assigned: (slot) => slotIds[slot],
    sample(slot): InputFrame {
      if (!polledThisTick) {
        polledThisTick = true;
        pollPads();
      }
      let f = 0;
      const devs = slotDevices[slot];
      for (let i = 0; i < devs.length; i++) f |= devs[i]!.sample();
      return f;
    },
    endTick() {
      keys.endSample();
      pads.endTick();
      touchState.endTick();
      polledThisTick = false;
    },
    onMenu(handler) {
      menuHandlers.add(handler);
      return () => void menuHandlers.delete(handler);
    },
    onAux(handler) {
      auxHandlers.add(handler);
      return () => void auxHandlers.delete(handler);
    },
    setBindings(deviceId, bindings) {
      if (deviceId === DEVICE_ID.TOUCH) return;
      putBindings(deviceId, bindings);
      touchSharedKeyboard(deviceId);
    },
    resetBindings(deviceId) {
      putBindings(deviceId, null);
      touchSharedKeyboard(deviceId);
    },
    bindingsFor: (deviceId) =>
      cloneBindings(effectiveKb.get(deviceId) ?? overrides.get(deviceId) ?? defaultBindingsFor(deviceId)),
    defaultBindings: (deviceId) => defaultBindingsFor(deviceId),
    loadBindings(saved) {
      const record = saved && typeof saved === 'object' ? saved : {};
      for (const id of [...overrides.keys()]) if (!(id in record)) putBindings(id, null);
      for (const [id, raw] of Object.entries(record)) {
        if (id === DEVICE_ID.TOUCH) continue;
        if (!keyboards.some((k) => k.id === id) && padIndexOf(id) < 0) continue;
        putBindings(id, sanitizeBindings(id, raw));
      }
      // Before any rebind this session (boot): the half with saved overrides keeps its keys.
      if (!kbTouched)
        kbWinner =
          overrides.has(DEVICE_ID.KB_P2) && !overrides.has(DEVICE_ID.KB_P1)
            ? DEVICE_ID.KB_P2
            : DEVICE_ID.KB_P1;
      applySharedKeyboard();
    },
    captureBinding(deviceId, options: CaptureOptions = {}) {
      finishCapture(null); // only one capture at a time
      return new Promise<string | null>((resolve) => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        // Hidden tab / no rAF: still settle a pending B hold.
        let bTimer: ReturnType<typeof setInterval> | null = null;
        const done = (code: string | null): void => {
          if (timer !== null) clearTimeout(timer);
          if (bTimer !== null) clearInterval(bTimer);
          options.signal?.removeEventListener('abort', onAbort);
          resolve(code);
        };
        const onAbort = (): void => {
          if (capture?.resolve === done) finishCapture(null);
        };
        capture = { deviceId, resolve: done };
        if (options.signal?.aborted) {
          finishCapture(null);
          return;
        }
        options.signal?.addEventListener('abort', onAbort);
        timer = setTimeout(() => {
          if (capture?.resolve === done) finishCapture(null);
        }, options.timeoutMs ?? 10_000);
        if (padIndexOf(deviceId) >= 0)
          bTimer = setInterval(() => {
            if (capture?.resolve !== done) return;
            pollPads();
            checkPendingB();
          }, 100);
      });
    },
    labelFor(deviceId, code) {
      const pi = padIndexOf(deviceId);
      return codeLabel(deviceId, code, pi >= 0 ? (pads.device(pi)?.family ?? 'generic') : 'generic');
    },
    promptFor(deviceId, button: ButtonName) {
      const b = manager.bindingsFor(deviceId);
      const code = b[button][0];
      return code ? manager.labelFor(deviceId, code) : '—';
    },
    lastActiveDevice: () => lastActive,
    setTouchMode(mode) {
      touchMode = mode === 'on' || mode === 'off' ? mode : 'auto';
      updateTouch();
    },
    touch: {
      get mode() {
        return touchMode;
      },
      get available() {
        return touch.connected;
      },
      get visible() {
        return overlay?.isVisible ?? false;
      },
    } satisfies TouchStatus,
    onTouchChanged(handler) {
      touchHandlers.add(handler);
      return () => void touchHandlers.delete(handler);
    },
    setTouchSuspended(s) {
      touchSuspended = s;
      updateTouch();
    },
    rumble(slot, strength, durationMs) {
      for (const d of slotDevices[slot])
        if (d.kind === 'gamepad' && d.connected)
          pads.rumble((d as GamepadDevice).index, strength, durationMs);
    },
    dispose() {
      if (frameId !== null) cancelFrame(frameId);
      frameId = null;
      finishCapture(null);
      target?.removeEventListener('keydown', onKeyDown);
      target?.removeEventListener('keydown', onGameKey, true);
      target?.removeEventListener('pointerdown', onFirstTouch, true);
      keys.dispose();
      pads.dispose();
      overlay?.dispose();
      overlay = null;
      menuHandlers.clear();
      auxHandlers.clear();
      deviceHandlers.clear();
      touchHandlers.clear();
    },
  };
  // Touch availability is known up front on phones.
  touch.connected = touchAvailable();
  touchSig = touchSignature();
  rebuildList();
  return manager;
};

/** All logical button names (re-exported for rebinding screens). */
export const LOGICAL_BUTTONS: readonly ButtonName[] = BUTTON_NAMES;
